import { Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import RedirectingScreen from "../../components/RedirectingScreen";
import ActionButtonSmall from "../../components/layout/buttons/action-button-small";
import { Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/auth";
import ApiUri from "../../api/api-uri";
import API from "../../api/api";
import BaseComponentLogin from "./baseComponentLogin";
import ReCAPTCHA from "react-google-recaptcha";
import { buildAppUrl, getCookie, setCookie } from "../../App";
import { SnackbarContext } from "../../context/snackbar";
import { TranslationsContext } from "../../context/translations";
import { InformationContext } from "../../context/information";

export const isValidEmail = (email) => {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(email);
};


const PROFILE_INFO_CACHE_TTL = 24 * 60 * 60 * 1000;

const Login = ({ app, profile }) => {
  const history = useHistory();
  const {
    setUser,
    setUserImageUrl,
    setJwt,
    profileNameLogin,
    setProfileNameLogin,
    profileImageURLLogin,
    setProfileImageURLLogin,
    profileUrlName,
    setProfileUrlName,
    showSignup,
    setShowSignup,
  } = useContext(AuthContext);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [loginError, setLoginError] = useState("");
  const { initSnackbarSuccess, initSnackbarError, initSnackbarGeneric } =
    useContext(SnackbarContext);
  const { translations, setTranslations, setHasFetchedTranslations } =
    useContext(TranslationsContext);
  const { setInformation } = useContext(InformationContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [recaptchaApproved, setRecaptchaApproved] = useState(false);
  const [hasFetchedProfileInfo, setHasFetchedProfileInfo] = useState(false);
  const [emailAutofilled, setEmailAutofilled] = useState(false);
  const [passwordAutofilled, setPasswordAutofilled] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isRedirecting, setIsRedirecting] = useState<{ url: string; appKey: string; profileName?: string; logoUrl?: string; pendingUser?: any; pendingJwt?: string } | null>(null);

  const location = useLocation();

  useEffect(() => {
    const state = location.state as { successMessage?: string; errorMessage?: string } | undefined;
    if (state?.successMessage) {
      setSuccessMessage(state.successMessage);
    }
    if (state?.errorMessage) {
      setLoginError(state.errorMessage);
    }
    if (state?.successMessage || state?.errorMessage) {
      history.replace(location.pathname, {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasFetchedProfileInfo) return;
    const check = () => {
      try {
        if (emailInputRef.current?.matches(":-webkit-autofill")) setEmailAutofilled(true);
        if (passwordInputRef.current?.matches(":-webkit-autofill")) setPasswordAutofilled(true);
      } catch (_) {}
    };
    const t1 = setTimeout(check, 100);
    const t2 = setTimeout(check, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [hasFetchedProfileInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAppAndProfileFromUrl = () => {
    const { pathname } = location;
    const parts = pathname.split("/").filter((part) => part);

    let appFromUrl, profileFromUrl;
    if (parts.length >= 2) {
      appFromUrl = parts[0];
      profileFromUrl = parts[1];
    }
    return { appFromUrl, profileFromUrl };
  };

  const buttonSX = {
    backgroundColor: "#3399FE",
    borderRadius: "8px",
    fontWeight: 600,
    letterSpacing: "0.4px",
    padding: "10px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#1a77d4",
      boxShadow: "0 6px 18px rgba(51,153,254,0.4)",
      transform: "translateY(-1px)",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(51,153,254,0.28)",
      color: "rgba(255,255,255,0.55)",
    },
  };

  document.title = profileNameLogin ? `${profileNameLogin} - Login` : "Login";

  const PROFILE_INFO_CACHE_KEY = `profileInfoCache_${app}_${profile}`;

  const applyProfileInfo = (data: any) => {
    if (data?.invitation?.email) {
      localStorage.setItem("emailForSignup", data.invitation.email);
    }
    setShowSignup(data.is_registration_enabled);
    setCookie(process.env.REACT_APP_WHITE_LABEL_COOKIE, data.white_label, 365);
    setInformation(data);
    setCookie("APP_NAME", data.app_name, 365);
    setCookie("WEB", data.web, 365);
    setCookie("DOMAIN_URL", data.domain, 365);
    setProfileImageURLLogin(data?.logo_url ?? "");
    setProfileNameLogin(data.profile_name ?? data.profile_information?.name);
    setProfileUrlName(data.url_name);
  };

  useEffect(() => {
    if (profileNameLogin && profileUrlName) {
      setHasFetchedProfileInfo(true);
      return;
    }

    const cached = localStorage.getItem(PROFILE_INFO_CACHE_KEY);
    if (cached) {
      try {
        const { data, cachedAt } = JSON.parse(cached);
        if (Date.now() - cachedAt < PROFILE_INFO_CACHE_TTL) {
          applyProfileInfo(data);
          const { profileFromUrl, appFromUrl } = getAppAndProfileFromUrl();
          if (profileFromUrl !== data.url_name) {
            history.replace(`/${appFromUrl ?? "mmdz"}/${data.url_name}/`);
          }
          setHasFetchedProfileInfo(true);
          return;
        }
      } catch {
        localStorage.removeItem(PROFILE_INFO_CACHE_KEY);
      }
    }

    setIsLoadingProfile(true);
    const tokenFromLocalStorage = localStorage.getItem("activationToken");
    let payload: any = {
      domain: window.location.hostname.split(".").slice(1).join("."),
      application: app,
      profile: profile,
    };
    if (tokenFromLocalStorage) {
      payload = { ...payload, token: tokenFromLocalStorage };
    }
    API.post(ApiUri.PROFILE + ApiUri.INFORMATION, payload).then(
      (response) => {
        const data = response.data.data;
        applyProfileInfo(data);
        localStorage.setItem(
          PROFILE_INFO_CACHE_KEY,
          JSON.stringify({ data, cachedAt: Date.now() }),
        );
        const { profileFromUrl, appFromUrl } = getAppAndProfileFromUrl();
        if (profileFromUrl !== data.url_name) {
          history.replace(`/${appFromUrl ?? "mmdz"}/${data.url_name}/`);
        }
        setIsLoadingProfile(false);
        setHasFetchedProfileInfo(true);
      },
      () => {
        setIsLoadingProfile(false);
        setHasFetchedProfileInfo(true);
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const logIn = (event) => {
    event.preventDefault();
    if (!email) {
      setLoginError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(email)) {
      setLoginError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setLoginError("Please enter your password.");
      return;
    }
    if (!recaptchaApproved) {
      setLoginError("Please complete the reCAPTCHA verification.");
      return;
    }
    setLoginError("");
    setIsLoadingLogin(true);
    const appCookie = getCookie(process.env.REACT_APP_MMAZ_APPLICATION_COOKIE);
    const profileCookie = getCookie(process.env.REACT_APP_MMAZ_PROFILE_COOKIE);
    const loginJSON = {
      email: email,
      password: password,
      application: appCookie,
      profile: profileCookie,
    };
    API.post(ApiUri.AUTH + ApiUri.LOGIN, loginJSON).then(
      (response) => {
        const jwtToken = response.data.token;
        setCookie(process.env.REACT_APP_MCZTOKEN_COOKIE, jwtToken, 14);
        const config = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        };
        API.get(ApiUri.MY_ACCOUNT, config).then(
          (response) => {
            const userFromEndpoint = response.data.data;
            const loginLocale = userFromEndpoint?.user_profile?.language?.code ?? "en";
            const loginCacheKey = `translationsCache_${loginLocale}`;
            const loginCached = localStorage.getItem(loginCacheKey);
            const loginCacheValid = (() => {
              if (!loginCached) return false;
              try {
                const { data, cachedAt } = JSON.parse(loginCached);
                if (Date.now() - cachedAt < 24 * 60 * 60 * 1000) {
                  setTranslations(data);
                  setHasFetchedTranslations(true);
                  return true;
                }
              } catch { localStorage.removeItem(loginCacheKey); }
              return false;
            })();
            const translationsFetch = loginCacheValid
              ? Promise.resolve(null)
              : API.get(ApiUri.PUBLIC + ApiUri.TRANSLATIONS, {
                  params: { locale: loginLocale },
                }).then((response) => {
                  const data = response.data.data;
                  localStorage.setItem(loginCacheKey, JSON.stringify({ data, cachedAt: Date.now() }));
                  setHasFetchedTranslations(true);
                  setTranslations(data);
                });
            translationsFetch.then(
              (response) => {
                API.get(ApiUri.USER + ApiUri.IMAGE, config).then(
                  (response) => {
                    const imageUrl = response.data.data.image;
                    setUserImageUrl(imageUrl);
                    const activationToken =
                      localStorage.getItem("activationToken");
                    const externalApps = ["mmdz", "mmpz"];
                    if (externalApps.includes(app) && !activationToken) {
                      setIsLoadingLogin(false);
                      setIsRedirecting({ url: buildAppUrl(app), appKey: app, profileName: profileNameLogin, logoUrl: profileImageURLLogin, pendingUser: userFromEndpoint, pendingJwt: jwtToken });
                      return;
                    }
                    setUser(userFromEndpoint);
                    setJwt(jwtToken);
                    if (activationToken) {
                      const formData = { token: activationToken };
                      API.post(
                        ApiUri.INVITATION + ApiUri.ACCEPT,
                        formData,
                        config,
                      ).then(
                        (response) => {
                          localStorage.removeItem("activationToken");
                          localStorage.removeItem("emailForSignup");
                          setIsLoadingLogin(false);
                          if (response?.data?.data?.redirect_url) {
                            window.location.replace(
                              response?.data?.data?.redirect_url,
                            );
                          } else {
                            redirectAfterLogin();
                          }
                        },
                        (error) => {
                          if (
                            error?.response?.data?.code === "login_required" ||
                            error?.response?.data?.code === "signup_required"
                          ) {
                            localStorage.setItem(
                              "activationToken",
                              activationToken,
                            );
                            const app =
                              error?.response?.data?.context?.application;
                            const profile =
                              error?.response?.data?.context?.profile;
                            if (
                              error?.response?.data?.code === "login_required"
                            ) {
                              initSnackbarGeneric(
                                error?.response?.data?.message,
                              );
                              initSnackbarError(error);
                              setIsLoadingLogin(false);
                              history.push("/dashboard");
                            } else if (
                              error?.response?.data?.code === "signup_required"
                            ) {
                              initSnackbarGeneric(
                                error?.response?.data?.message,
                              );
                              setIsLoadingLogin(false);
                              history.push("/dashboard");
                            }
                          } else if (
                            error?.response?.data?.code === "accepted" ||
                            error?.response?.data?.code === "invalid_invitation"
                          ) {
                            initSnackbarError(error?.response?.data?.message);
                            setIsLoadingLogin(false);
                            history.push("/dashboard");
                          } else {
                            initSnackbarError(error?.response?.data?.message);
                            setIsLoadingLogin(false);
                            history.push("/dashboard");
                          }
                        },
                      );
                    } else {
                      setIsLoadingLogin(false);
                      redirectAfterLogin();
                    }
                  },
                  (error) => {
                    setLoginError("Login failed. Please try again.");
                    setIsLoadingLogin(false);
                  },
                );
              },
              (error) => {
                setLoginError("Login failed. Please try again.");
                setIsLoadingLogin(false);
              },
            );
          },
          (error) => {
            setLoginError("Login failed. Please try again.");
            setIsLoadingLogin(false);
          },
        );
      },
      (error) => {
        setLoginError(error?.response?.data?.message ?? "Login failed. Please try again.");
        setIsLoadingLogin(false);
      },
    );
    // history.push("/");
  };

  function onChangeRecaptcha(value) {
    if (value) {
      setRecaptchaApproved(true);
    } else {
      setRecaptchaApproved(false);
    }
  }

  const redirectAfterLogin = () => {
    const externalApps = ["mmdz", "mmpz"];
    if (externalApps.includes(app)) {
      window.location.replace(buildAppUrl(app));
    } else {
      history.push("/dashboard");
    }
  };

  const handleBlur = () => {
    setEmailError(!isValidEmail(email));
  };

  const emailIsValid = isValidEmail(email);

  const handleChangeEmail = (event) => {
    const input = event.target.value.trim();
    setEmail(input);
    if (loginError) setLoginError("");
    if (emailError) {
      setEmailError(!isValidEmail(input));
    }
  };

  const headerContent = hasFetchedProfileInfo ? (
    <Stack alignItems="center" spacing={1.5}>
      <img
        src={profileImageURLLogin}
        alt={profileNameLogin}
        style={{
          width: "76px",
          height: "76px",
          borderRadius: "50%",
          border: "2px solid rgba(51,153,254,0.55)",
          boxShadow: "0 0 0 5px rgba(51,153,254,0.12), 0 8px 24px rgba(0,0,0,0.4)",
        }}
      />
      <Box>
        <Typography
          align="center"
          variant="h5"
          sx={{
            wordBreak: "break-word",
            fontWeight: 700,
            color: "#ffffff",
            letterSpacing: "-0.3px",
            lineHeight: 1.2,
          }}
        >
          {profileNameLogin}
        </Typography>
        <Typography
          align="center"
          sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: "10px",
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            mt: 0.5,
          }}
        >
          Log In
        </Typography>
      </Box>
    </Stack>
  ) : null;

  if (isRedirecting) {
    return (
      <RedirectingScreen
        url={isRedirecting.url}
        appKey={isRedirecting.appKey}
        profileName={isRedirecting.profileName}
        logoUrl={isRedirecting.logoUrl}
        onCancel={() => { setUser(isRedirecting.pendingUser); setJwt(isRedirecting.pendingJwt); history.push("/dashboard"); }}
      />
    );
  }

  return (
    <BaseComponentLogin headerSection={headerContent} isLoading={isLoadingProfile}>
      {hasFetchedProfileInfo && (
        <>
          <Stack spacing={1.5} width={"100%"}>
            <TextField
              fullWidth
              size="small"
              label={translations["label.email"] || "Email"}
              value={email}
              onChange={handleChangeEmail}
              sx={{
                paddingBottom: 1,
                "&:has(input:-webkit-autofill) .MuiInputLabel-root": {
                  transform: "translate(14px, -9px) scale(0.75)",
                  maxWidth: "calc(133% - 24px)",
                },
                "&:has(input:-webkit-autofill) fieldset legend span": {
                  maxWidth: "100%",
                  padding: "0 5px",
                },
              }}
              onBlur={handleBlur}
              error={emailError}
              autoComplete="email"
              inputRef={emailInputRef}
              inputProps={{ autoCapitalize: "none" }}
              InputLabelProps={{ shrink: emailAutofilled || !!email || undefined }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label={translations["label.password"] || "Password"}
              value={password}
              onChange={(event) => { setPassword(event.target.value); if (loginError) setLoginError(""); }}
              autoComplete="current-password"
              inputRef={passwordInputRef}
              sx={{
                "&:has(input:-webkit-autofill) .MuiInputLabel-root": {
                  transform: "translate(14px, -9px) scale(0.75)",
                  maxWidth: "calc(133% - 24px)",
                },
                "&:has(input:-webkit-autofill) fieldset legend span": {
                  maxWidth: "100%",
                  padding: "0 5px",
                },
              }}
              InputLabelProps={{ shrink: passwordAutofilled || !!password || undefined }}
            />
            <Box alignSelf="flex-end">
              <Link
                to={`/${app}/${profile}/forgotten-password`}
                className="login-link"
              >
                {translations["button.forgot_password"] || "Forgot password?"}
              </Link>
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_SITEKEY_RECAPTCHA}
              onChange={onChangeRecaptcha}
            />
          </Box>

          {loginError && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "rgba(211,47,47,0.08)",
                border: "1px solid rgba(211,47,47,0.25)",
              }}
            >
              <Typography
                fontSize={13}
                sx={{ color: "#c62828", textAlign: "center" }}
              >
                {loginError}
              </Typography>
            </Box>
          )}

          {successMessage && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "rgba(46,125,50,0.08)",
                border: "1px solid rgba(46,125,50,0.25)",
              }}
            >
              <Typography
                fontSize={13}
                sx={{ color: "#2e7d32", textAlign: "center" }}
              >
                {successMessage}
              </Typography>
            </Box>
          )}

          <ActionButtonSmall
            sx={{ ...buttonSX, mt: 2 }}
            onClick={logIn}
            disabled={isLoadingLogin}
            fullWidth
            ignoreStyles={true}
          >
            {isLoadingLogin ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.7)" }} />
                <span>{translations["label.please_wait"] || "Please wait..."}</span>
              </Box>
            ) : (
              translations["button.log_in"] || "Log In"
            )}
          </ActionButtonSmall>

          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: "1px solid #f0f0f0",
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography fontSize={12} sx={{ color: "#aaa" }}>
              {translations["label.no_activation_email"] ||
                "Haven't received the activation email?"}{" "}
              <Link
                to={`/${app}/${profile}/activation/resend`}
                className="login-link"
                style={{ fontSize: "12px" }}
              >
                {translations["button.resend_activation_email"] ||
                  "Resend"}
              </Link>
            </Typography>
            {showSignup && (
              <Typography fontSize={13} sx={{ mt: 1.5, color: "#555" }}>
                {translations["help.no_account"] ||
                  "Don't have an account yet?"}{" "}
                <Link to={`/${app}/signup/${profile}`} className="login-link">
                  {translations["button.sign_up"] || "Sign up"}
                </Link>
              </Typography>
            )}
          </Box>
        </>
      )}
    </BaseComponentLogin>
  );
};

export default Login;
